import { useCallback, useState } from "react";

/**
 * Булев флаг в localStorage. Для состояния уровня «панель открыта»: это
 * настройка конкретного браузера, а не урока, и в Y.Doc ей не место.
 *
 * localStorage бросает исключение в приватном режиме Safari и при
 * переполнении квоты. Отладочный тумблер не повод ронять комнату.
 */
export function usePersistentFlag(
  key: string,
  fallback: boolean,
): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? fallback : raw === "1";
    } catch {
      return fallback;
    }
  });

  const set = useCallback(
    (next: boolean) => {
      setValue(next);
      try {
        localStorage.setItem(key, next ? "1" : "0");
      } catch {
        /* приватный режим: живём без запоминания */
      }
    },
    [key],
  );

  return [value, set];
}

/**
 * То же самое, но для выбора из нескольких значений: угол видео, режим панели.
 *
 * Разбор приезжает функцией, а не списком допустимых строк, по той же причине,
 * по которой у флага есть `fallback`: решать, что делать с непонятным хранимым
 * значением, обязана сторона, которая знает смысл. Здесь известно только то,
 * что упасть нельзя.
 *
 * ⚠️ Разбор зовётся и при чтении, и при записи. Записывать неразобранное
 * значило бы сохранить мусор, который в следующий раз всё равно не прочитается.
 */
export function usePersistentChoice<T extends string>(
  key: string,
  parse: (raw: string | null) => T,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      return parse(localStorage.getItem(key));
    } catch {
      return parse(null);
    }
  });

  const set = useCallback(
    (next: T) => {
      const safe = parse(next);
      setValue(safe);
      try {
        localStorage.setItem(key, safe);
      } catch {
        /* приватный режим: живём без запоминания */
      }
    },
    [key, parse],
  );

  return [value, set];
}
